import { Store } from "@ngrx/store";
import { Observable } from "rxjs";
import { HttpHeaders, HttpClient } from "@angular/common/http";

import * as M from "../../data/model";
import { config, version } from "../../site-local";
import { UpdateCache } from "./update-cache.service";

export class PersistenceEntities {
    protected serviceUrl = config.ZBDSERVICEURL;

    protected trace = false;
    protected reqOpts: {
        headers?: HttpHeaders;
        observe: "body";
        responseType: "json";
    };

    constructor(
        protected http: HttpClient,
        protected updateCache: UpdateCache,
    ) {
        const headers = new HttpHeaders()
            .append("Content-Type", "application/json")
            .append("Accept", "application/json");
        this.reqOpts = {
            headers: headers,
            observe: "body",
            responseType: "json",
        };
    }

    public updateAddress = (
        address: M.Address,
        property: keyof M.Address,
        value: any,
        flush = false,
    ): void => {
        this.updateCache.update("Addresses", address, property, value, flush);
    };

    public deleteAddress = (address: M.Address): void => {
        this.updateCache.remove("Addresses", address);
    };

    public updateAgent = (
        agent: M.Agent,
        property: keyof M.Agent,
        value: any,
        flush = false,
    ): void => {
        this.updateCache.update("Agents", agent, property, value, flush);
    };

    public deleteAgent = (agent: M.Agent): void => {
        this.updateCache.remove("Agents", agent);
    };

    public updateAttachment = (
        attachment: M.Attachment,
        property: keyof M.Attachment,
        value: any,
        flush = false,
    ): void => {
        this.updateCache.update(
            "Attachments",
            attachment,
            property,
            value,
            flush,
        );
    };

    public deleteAttachment = (attachment: M.Attachment): void => {
        this.updateCache.remove("Attachments", attachment);
    };

    public updateCancellation = (
        cancellation: M.Cancellation,
        property: keyof M.Cancellation,
        value: any,
        flush = false,
    ): void => {
        this.updateCache.update(
            "Cancellations",
            cancellation,
            property,
            value,
            flush,
        );
    };

    public deleteCancellation = (cancellation: M.Cancellation): void => {
        this.updateCache.remove("Cancellations", cancellation);
    };

    public updateCompany = (
        company: M.Company,
        property: keyof M.Company,
        value: any,
        flush = false,
    ): void => {
        this.updateCache.update("Companies", company, property, value, flush);
    };

    public deleteCompany = (company: M.Company): void => {
        this.updateCache.remove("Companies", company);
    };

    public updateConsultationDate = (
        consultationDate: M.ConsultationDate,
        property: keyof M.ConsultationDate,
        value: any,
        flush = false,
    ): void => {
        this.updateCache.update(
            "ConsultationDates",
            consultationDate,
            property,
            value,
            flush,
        );
    };

    public deleteConsultationDate = (
        consultationDate: M.ConsultationDate,
    ): void => {
        this.updateCache.remove("ConsultationDates", consultationDate);
    };

    public loadConsultationProcess = (consultationProcessId: number): void => {
        this.updateCache.load("ConsultationProcesses", consultationProcessId);
    };

    public addConsultationProcess = (
        consultationProcess: M.ConsultationProcess,
    ): void => {
        this.updateCache.addDetached(
            "ConsultationProcesses",
            consultationProcess,
        );
    };

    public updateConsultationProcess = (
        consultationProcess: M.ConsultationProcess,
        property: keyof M.ConsultationProcess,
        value: any,
        flush = false,
    ): void => {
        this.updateCache.update(
            "ConsultationProcesses",
            consultationProcess,
            property,
            value,
            flush,
        );
    };

    public deleteConsultationProcess = (
        consultationProcess: M.ConsultationProcess,
    ): void => {
        this.updateCache.remove("ConsultationProcesses", consultationProcess);
    };

    public addConsultationProcess_ConsultationDates = (
        consultationProcess: M.ConsultationProcess,
        consultationDate: M.ConsultationDate,
    ): void => {
        this.updateCache.add(
            "ConsultationProcesses",
            consultationProcess,
            "ConsultationDates",
            "ConsultationDates",
            consultationDate,
        );
    };

    public updateDecision = (
        decision: M.Decision,
        property: keyof M.Decision,
        value: any,
        flush = false,
    ): void => {
        this.updateCache.update("Decisions", decision, property, value, flush);
    };

    public deleteDecision = (decision: M.Decision): void => {
        this.updateCache.remove("Decisions", decision);
    };

    public updateDemand = (
        demand: M.Demand,
        property: keyof M.Demand,
        value: any,
        flush = false,
    ): void => {
        this.updateCache.update("Demands", demand, property, value, flush);
    };

    public deleteDemand = (demand: M.Demand): void => {
        this.updateCache.remove("Demands", demand);
    };

    public updateDivision = (
        division: M.Division,
        property: keyof M.Division,
        value: any,
        flush = false,
    ): void => {
        this.updateCache.update("Divisions", division, property, value, flush);
    };

    public deleteDivision = (division: M.Division): void => {
        this.updateCache.remove("Divisions", division);
    };

    public addDivision_Events = (
        division: M.Division,
        event: M.Event,
    ): void => {
        this.updateCache.add("Divisions", division, "Events", "Events", event);
    };

    public updateDocument = (
        document: M.Document,
        property: keyof M.Document,
        value: any,
        flush = false,
    ): void => {
        this.updateCache.update("Documents", document, property, value, flush);
    };

    public deleteDocument = (document: M.Document): void => {
        this.updateCache.remove("Documents", document);
    };

    public updateEvent = (
        event: M.Event,
        property: keyof M.Event,
        value: any,
        flush = false,
    ): void => {
        this.updateCache.update("Events", event, property, value, flush);
    };

    public deleteEvent = (event: M.Event): void => {
        this.updateCache.remove("Events", event);
    };

    public updateExemption = (
        exemption: M.Exemption,
        property: keyof M.Exemption,
        value: any,
        flush = false,
    ): void => {
        this.updateCache.update(
            "Exemptions",
            exemption,
            property,
            value,
            flush,
        );
    };

    public deleteExemption = (exemption: M.Exemption): void => {
        this.updateCache.remove("Exemptions", exemption);
    };

    public updateFileData = (
        fileData: M.FileData,
        property: keyof M.FileData,
        value: any,
        flush = false,
    ): void => {
        this.updateCache.update("FileDatas", fileData, property, value, flush);
    };

    public deleteFileData = (fileData: M.FileData): void => {
        this.updateCache.remove("FileDatas", fileData);
    };

    public updateGlobalOccasion = (
        globalOccasion: M.GlobalOccasion,
        property: keyof M.GlobalOccasion,
        value: any,
        flush = false,
    ): void => {
        this.updateCache.update(
            "GlobalOccasions",
            globalOccasion,
            property,
            value,
            flush,
        );
    };

    public deleteGlobalOccasion = (globalOccasion: M.GlobalOccasion): void => {
        this.updateCache.remove("GlobalOccasions", globalOccasion);
    };

    public addGlobalOccasion_ConsultationDates = (
        globalOccasion: M.GlobalOccasion,
        consultationDate: M.ConsultationDate,
    ): void => {
        this.updateCache.add(
            "GlobalOccasions",
            globalOccasion,
            "ConsultationDates",
            "ConsultationDates",
            consultationDate,
        );
    };

    public addGlobalOccasion_Attachments = (
        globalOccasion: M.GlobalOccasion,
        attachment: M.Attachment,
    ): void => {
        this.updateCache.add(
            "GlobalOccasions",
            globalOccasion,
            "Attachments",
            "Attachments",
            attachment,
        );
    };

    public addGlobalOccasion_GlobalOccasionSections = (
        globalOccasion: M.GlobalOccasion,
        globalOccasionSection: M.GlobalOccasionSection,
    ): void => {
        this.updateCache.add(
            "GlobalOccasions",
            globalOccasion,
            "GlobalOccasionSections",
            "GlobalOccasionSections",
            globalOccasionSection,
        );
    };

    public updateGlobalOccasionEntry = (
        globalOccasionEntry: M.GlobalOccasionEntry,
        property: keyof M.GlobalOccasionEntry,
        value: any,
        flush = false,
    ): void => {
        this.updateCache.update(
            "GlobalOccasionEntries",
            globalOccasionEntry,
            property,
            value,
            flush,
        );
    };

    public deleteGlobalOccasionEntry = (
        globalOccasionEntry: M.GlobalOccasionEntry,
    ): void => {
        this.updateCache.remove("GlobalOccasionEntries", globalOccasionEntry);
    };

    public updateGlobalOccasionSection = (
        globalOccasionSection: M.GlobalOccasionSection,
        property: keyof M.GlobalOccasionSection,
        value: any,
        flush = false,
    ): void => {
        this.updateCache.update(
            "GlobalOccasionSections",
            globalOccasionSection,
            property,
            value,
            flush,
        );
    };

    public deleteGlobalOccasionSection = (
        globalOccasionSection: M.GlobalOccasionSection,
    ): void => {
        this.updateCache.remove(
            "GlobalOccasionSections",
            globalOccasionSection,
        );
    };

    public addGlobalOccasionSection_GlobalOccasionEntries = (
        globalOccasionSection: M.GlobalOccasionSection,
        globalOccasionEntry: M.GlobalOccasionEntry,
    ): void => {
        this.updateCache.add(
            "GlobalOccasionSections",
            globalOccasionSection,
            "GlobalOccasionEntries",
            "GlobalOccasionEntries",
            globalOccasionEntry,
        );
    };

    public updateIndividual = (
        individual: M.Individual,
        property: keyof M.Individual,
        value: any,
        flush = false,
    ): void => {
        this.updateCache.update(
            "Individuals",
            individual,
            property,
            value,
            flush,
        );
    };

    public deleteIndividual = (individual: M.Individual): void => {
        this.updateCache.remove("Individuals", individual);
    };

    public updateOccasionOccasionEntry = (
        occasionOccasionEntry: M.OccasionOccasionEntry,
        property: keyof M.OccasionOccasionEntry,
        value: any,
        flush = false,
    ): void => {
        this.updateCache.update(
            "OccasionOccasionEntries",
            occasionOccasionEntry,
            property,
            value,
            flush,
        );
    };

    public deleteOccasionOccasionEntry = (
        occasionOccasionEntry: M.OccasionOccasionEntry,
    ): void => {
        this.updateCache.remove(
            "OccasionOccasionEntries",
            occasionOccasionEntry,
        );
    };

    public updateOffer = (
        offer: M.Offer,
        property: keyof M.Offer,
        value: any,
        flush = false,
    ): void => {
        this.updateCache.update("Offers", offer, property, value, flush);
    };

    public deleteOffer = (offer: M.Offer): void => {
        this.updateCache.remove("Offers", offer);
    };

    public addOffer_Divisions = (
        offer: M.Offer,
        division: M.Division,
    ): void => {
        this.updateCache.add(
            "Offers",
            offer,
            "Divisions",
            "Divisions",
            division,
        );
    };

    public updateParticipant = (
        participant: M.Participant,
        property: keyof M.Participant,
        value: any,
        flush = false,
    ): void => {
        this.updateCache.update(
            "Participants",
            participant,
            property,
            value,
            flush,
        );
    };

    public deleteParticipant = (participant: M.Participant): void => {
        this.updateCache.remove("Participants", participant);
    };

    public updateProductOccasionEntry = (
        productOccasionEntry: M.ProductOccasionEntry,
        property: keyof M.ProductOccasionEntry,
        value: any,
        flush = false,
    ): void => {
        this.updateCache.update(
            "ProductOccasionEntries",
            productOccasionEntry,
            property,
            value,
            flush,
        );
    };

    public deleteProductOccasionEntry = (
        productOccasionEntry: M.ProductOccasionEntry,
    ): void => {
        this.updateCache.remove("ProductOccasionEntries", productOccasionEntry);
    };

    public updateProductOccasionSection = (
        productOccasionSection: M.ProductOccasionSection,
        property: keyof M.ProductOccasionSection,
        value: any,
        flush = false,
    ): void => {
        this.updateCache.update(
            "ProductOccasionSections",
            productOccasionSection,
            property,
            value,
            flush,
        );
    };

    public deleteProductOccasionSection = (
        productOccasionSection: M.ProductOccasionSection,
    ): void => {
        this.updateCache.remove(
            "ProductOccasionSections",
            productOccasionSection,
        );
    };

    public addProductOccasionSection_ProductOccasionEntries = (
        productOccasionSection: M.ProductOccasionSection,
        productOccasionEntry: M.ProductOccasionEntry,
    ): void => {
        this.updateCache.add(
            "ProductOccasionSections",
            productOccasionSection,
            "ProductOccasionEntries",
            "ProductOccasionEntries",
            productOccasionEntry,
        );
    };

    public updateProtocol = (
        protocol: M.Protocol,
        property: keyof M.Protocol,
        value: any,
        flush = false,
    ): void => {
        this.updateCache.update("Protocols", protocol, property, value, flush);
    };

    public deleteProtocol = (protocol: M.Protocol): void => {
        this.updateCache.remove("Protocols", protocol);
    };

    public addProtocol_Offers = (
        protocol: M.Protocol,
        offer: M.Offer,
    ): void => {
        this.updateCache.add("Protocols", protocol, "Offers", "Offers", offer);
    };

    public addProtocol_Attachments = (
        protocol: M.Protocol,
        attachment: M.Attachment,
    ): void => {
        this.updateCache.add(
            "Protocols",
            protocol,
            "Attachments",
            "Attachments",
            attachment,
        );
    };

    public addProtocol_Participants = (
        protocol: M.Protocol,
        participant: M.Participant,
    ): void => {
        this.updateCache.add(
            "Protocols",
            protocol,
            "Participants",
            "Participants",
            participant,
        );
    };

    public addProtocol_ProductOccasionSections = (
        protocol: M.Protocol,
        productOccasionSection: M.ProductOccasionSection,
    ): void => {
        this.updateCache.add(
            "Protocols",
            protocol,
            "ProductOccasionSections",
            "ProductOccasionSections",
            productOccasionSection,
        );
    };

    public addProtocol_OccasionOccasionEntries = (
        protocol: M.Protocol,
        occasionOccasionEntry: M.OccasionOccasionEntry,
    ): void => {
        this.updateCache.add(
            "Protocols",
            protocol,
            "OccasionOccasionEntries",
            "OccasionOccasionEntries",
            occasionOccasionEntry,
        );
    };

    public addProtocol_SignatureInfos = (
        protocol: M.Protocol,
        signatureInfo: M.SignatureInfo,
    ): void => {
        this.updateCache.add(
            "Protocols",
            protocol,
            "SignatureInfos",
            "SignatureInfos",
            signatureInfo,
        );
    };

    public updateRecommendation = (
        recommendation: M.Recommendation,
        property: keyof M.Recommendation,
        value: any,
        flush = false,
    ): void => {
        this.updateCache.update(
            "Recommendations",
            recommendation,
            property,
            value,
            flush,
        );
    };

    public deleteRecommendation = (recommendation: M.Recommendation): void => {
        this.updateCache.remove("Recommendations", recommendation);
    };

    public addRecommendation_RecommendedCancellations = (
        recommendation: M.Recommendation,
        recommendedCancellation: M.Cancellation,
    ): void => {
        this.updateCache.add(
            "Recommendations",
            recommendation,
            "RecommendedCancellations",
            "Cancellations",
            recommendedCancellation,
        );
    };

    public addRecommendation_RecommendedExemptions = (
        recommendation: M.Recommendation,
        recommendedExemption: M.Exemption,
    ): void => {
        this.updateCache.add(
            "Recommendations",
            recommendation,
            "RecommendedExemptions",
            "Exemptions",
            recommendedExemption,
        );
    };

    public updateSignatureInfo = (
        signatureInfo: M.SignatureInfo,
        property: keyof M.SignatureInfo,
        value: any,
        flush = false,
    ): void => {
        this.updateCache.update(
            "SignatureInfos",
            signatureInfo,
            property,
            value,
            flush,
        );
    };

    public deleteSignatureInfo = (signatureInfo: M.SignatureInfo): void => {
        this.updateCache.remove("SignatureInfos", signatureInfo);
    };

    public updateTextblock = (
        textblock: M.Textblock,
        property: keyof M.Textblock,
        value: any,
        flush = false,
    ): void => {
        this.updateCache.update(
            "Textblocks",
            textblock,
            property,
            value,
            flush,
        );
    };

    public deleteTextblock = (textblock: M.Textblock): void => {
        this.updateCache.remove("Textblocks", textblock);
    };

    public updateTrackingText = (
        trackingText: M.TrackingText,
        property: keyof M.TrackingText,
        value: any,
        flush = false,
    ): void => {
        this.updateCache.update(
            "TrackingTexts",
            trackingText,
            property,
            value,
            flush,
        );
    };

    public deleteTrackingText = (trackingText: M.TrackingText): void => {
        this.updateCache.remove("TrackingTexts", trackingText);
    };

    public addTrackingText_Events = (
        trackingText: M.TrackingText,
        event: M.Event,
    ): void => {
        this.updateCache.add(
            "TrackingTexts",
            trackingText,
            "Events",
            "Events",
            event,
        );
    };
}
